import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import { PlusCircle, Search, Copy, Trash2 } from "lucide-react";
import { formatDate } from "@/lib/utils";

export default function OrdersList() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  // Read initial filter state from URL so filters persist after navigating back
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("to") || "");
  const navigate = useNavigate();
  const isCustomer = user?.role === "customer";
  const isAdmin = user?.role === "admin";
  const basePath = isCustomer ? "/portal/orders" : "/admin/orders";

  // Keep URL in sync with the filters (replace, no history spam)
  useEffect(() => {
    const next = {};
    if (q) next.q = q;
    if (statusFilter) next.status = statusFilter;
    if (dateFrom) next.from = dateFrom;
    if (dateTo) next.to = dateTo;
    setSearchParams(next, { replace: true });
  }, [q, statusFilter, dateFrom, dateTo, setSearchParams]);

  const fetchOrders = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    const { data } = await api.get("/orders", { params });
    setOrders(data);
  };

  useEffect(() => { fetchOrders(); /* eslint-disable-next-line */ }, [statusFilter, dateFrom, dateTo]);

  const filtered = orders.filter((o) =>
    !q ||
    o.order_number?.toLowerCase().includes(q.toLowerCase()) ||
    o.reference_number?.toLowerCase().includes(q.toLowerCase()) ||
    o.customer_order_number?.toLowerCase().includes(q.toLowerCase()) ||
    o.customer_name?.toLowerCase().includes(q.toLowerCase()) ||
    o.delivery?.city?.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Layout>
      <div className="px-6 md:px-10 py-8 max-w-[1400px] mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-eyebrow" style={{ color: "#D4AF37" }}>Aufträge</div>
            <h1 className="text-3xl md:text-4xl font-semibold tracking-tight mt-2">
              {isCustomer ? "Meine Aufträge" : "Alle Aufträge"}
            </h1>
          </div>
          <button
            onClick={() => navigate(`${basePath}/new`)}
            data-testid="orders-create-button"
            className="btn-gold px-5 py-3 rounded-sm text-sm uppercase tracking-wider flex items-center gap-2"
          >
            <PlusCircle size={16} strokeWidth={1.8} /> Neuer Auftrag
          </button>
        </div>

        {/* Filters */}
        <div className="mt-6 flex flex-col md:flex-row gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={15} strokeWidth={1.5} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--text-3)] pointer-events-none" />
            <input
              data-testid="orders-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Auftrag, Referenz, Bestell-Nr., Kunde oder Ort suchen…"
              className="input-base !pl-11"
            />
          </div>
          <select
            data-testid="orders-status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-base md:w-44"
          >
            <option value="">Alle Status</option>
            <option value="erstellt">Erstellt</option>
            <option value="geplant">Geplant</option>
            <option value="abgeholt">Abgeholt</option>
            <option value="zwischenlager">Zwischenlager</option>
            <option value="unterwegs">Unterwegs</option>
            <option value="zugestellt">Zugestellt</option>
            <option value="storniert">Storniert</option>
          </select>
          <div className="flex items-center gap-2 flex-wrap w-full md:w-auto">
            <span className="text-[10px] uppercase tracking-wider text-[var(--text-3)]">Lieferdatum</span>
            <input
              type="date"
              data-testid="orders-date-from"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="input-base w-[132px] min-w-0 md:w-40"
              title="Von (Lieferdatum)"
            />
            <span className="text-[var(--text-3)]">–</span>
            <input
              type="date"
              data-testid="orders-date-to"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="input-base w-[132px] min-w-0 md:w-40"
              title="Bis (Lieferdatum)"
            />
            {(dateFrom || dateTo || statusFilter || q) && (
              <button
                type="button"
                onClick={() => { setDateFrom(""); setDateTo(""); setStatusFilter(""); setQ(""); }}
                data-testid="orders-filters-reset"
                className="text-[10px] uppercase tracking-wider text-[var(--text-2)] hover:text-[#D4AF37] px-2 py-1 border border-[var(--border)] hover:border-[#D4AF37]/40 rounded-sm"
              >
                Zurücksetzen
              </button>
            )}
          </div>
        </div>

        {/* Mobile: Karten-Ansicht (kein horizontales Scrollen) */}
        <div className="mt-6 space-y-2 md:hidden" data-testid="orders-mobile-list">
          {filtered.length === 0 && (
            <div className="card p-8 text-center text-[var(--text-3)] text-sm">Keine Aufträge gefunden.</div>
          )}
          {filtered.map((o) => (
            <button
              key={o.id}
              onClick={() => navigate(`${basePath}/${o.id}`)}
              data-testid={`order-card-${o.order_number}`}
              className="card w-full p-3.5 text-left hover:border-[#D4AF37]/40 active:bg-[#D4AF37]/[0.04] transition-colors"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-mono text-sm text-[var(--text)]">{o.order_number}</div>
                  {!isCustomer && <div className="text-sm text-[var(--text)] truncate mt-0.5">{o.customer_name || "—"}</div>}
                  <div className="text-sm text-[var(--text-2)] truncate mt-0.5">{o.delivery?.zip} {o.delivery?.city}</div>
                  {(o.reference_number || o.customer_order_number) && (
                    <div className="text-[11px] text-[var(--text-3)] truncate mt-0.5">
                      {o.reference_number ? `Ref: ${o.reference_number}` : ""}{o.reference_number && o.customer_order_number ? " · " : ""}{o.customer_order_number ? `Best.-Nr.: ${o.customer_order_number}` : ""}
                    </div>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <StatusBadge status={o.status} />
                    {o.status === "zwischenlager" && o.warehouse_location && (
                      <span className="text-[11px] text-orange-800 font-medium">{o.warehouse_location}</span>
                    )}
                    <span className="text-[11px] text-[var(--text-3)]">{formatDate(o.delivery_date)}</span>
                    <span className="text-[11px] text-[var(--text-3)]">· {o.pallet_count} Pal.</span>
                    <span className="text-[11px] text-[var(--text-3)]">· {o.weight_kg} kg</span>
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Desktop/Tablet: Tabelle */}
        <div className="mt-6 card overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[800px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-[var(--text-3)] uppercase text-[11px] tracking-wider">
                <th className="text-left py-3 px-4">Auftrag</th>
                <th className="text-left py-3 px-4">Referenz</th>
                {!isCustomer && <th className="text-left py-3 px-4">Kunde</th>}
                <th className="text-left py-3 px-4">Lieferort</th>
                <th className="text-left py-3 px-4">Lieferdatum</th>
                <th className="text-left py-3 px-4">Paletten</th>
                <th className="text-left py-3 px-4">Gewicht</th>
                <th className="text-left py-3 px-4">Status</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={isCustomer ? 8 : 9} className="text-center py-12 text-[var(--text-3)]" data-testid="orders-empty">Keine Aufträge gefunden.</td></tr>
              )}
              {filtered.map((o) => (
                <tr
                  key={o.id}
                  onClick={() => navigate(`${basePath}/${o.id}`)}
                  className="border-b border-[var(--border)] hover:bg-[var(--surface-2)] cursor-pointer transition-colors"
                  data-testid={`order-row-${o.order_number}`}
                >
                  <td className="py-3 px-4 font-mono text-[var(--text)]">{o.order_number}</td>
                  <td className="py-3 px-4 text-[var(--text-2)]">
                    <div>{o.reference_number || "—"}</div>
                    {o.customer_order_number && (
                      <div className="text-[10px] uppercase tracking-wider text-[var(--text-3)] mt-0.5">
                        Best.-Nr.: <span className="text-[var(--text)] font-mono normal-case tracking-normal">{o.customer_order_number}</span>
                      </div>
                    )}
                  </td>
                  {!isCustomer && <td className="py-3 px-4 text-[var(--text)]">{o.customer_name || "—"}</td>}
                  <td className="py-3 px-4 text-[var(--text)]">{o.delivery?.zip} {o.delivery?.city}</td>
                  <td className="py-3 px-4 text-[var(--text)]">{formatDate(o.delivery_date)}</td>
                  <td className="py-3 px-4 font-mono text-[var(--text)]">{o.pallet_count}</td>
                  <td className="py-3 px-4 font-mono text-[var(--text-2)]">{o.weight_kg} kg</td>
                  <td className="py-3 px-4">
                    <StatusBadge status={o.status} />
                    {o.status === "zwischenlager" && o.warehouse_location && (
                      <div className="text-[10px] text-[var(--text-3)] mt-1" data-testid={`order-warehouse-${o.id}`}>{o.warehouse_location}</div>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right whitespace-nowrap">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm("Auftrag duplizieren?")) return;
                        try {
                          const { data } = await api.post(`/orders/${o.id}/duplicate`);
                          navigate(`${basePath}/${data.id}`);
                        } catch { /* noop */ }
                      }}
                      data-testid={`order-duplicate-row-${o.order_number}`}
                      title="Auftrag duplizieren"
                      className="text-[var(--text-3)] hover:text-[#D4AF37] mr-3"
                    >
                      <Copy size={16} strokeWidth={1.5} />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!window.confirm(`Auftrag ${o.order_number} wirklich löschen?\nDieser Vorgang kann nicht rückgängig gemacht werden.`)) return;
                          try {
                            await api.delete(`/orders/${o.id}`);
                            fetchOrders();
                          } catch (err) {
                            window.alert("Fehler beim Löschen: " + (err?.response?.data?.detail || err.message));
                          }
                        }}
                        data-testid={`order-delete-row-${o.order_number}`}
                        title="Auftrag löschen"
                        className="text-[var(--text-3)] hover:text-red-400"
                      >
                        <Trash2 size={16} strokeWidth={1.5} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
